/**
 * ConfirmProvider + useConfirm — confirmação via modal (substitui window.confirm).
 *
 * Motivo (incidente 2026-07-25): window.confirm foge do tema, não é testável por
 * data-testid e quebra a consistência visual. Este provider expõe um `confirm()`
 * promise-based que renderiza UMA instância do DeleteConfirmationModal (já portalado
 * pro document.body). O call-site troca `if (!window.confirm(msg)) return;` por
 * `if (!(await confirm({ message: msg }))) return;` — mudança mínima.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   if (await confirm({ message: 'Excluir X?', confirmButtonText: 'Excluir' })) { ... }
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

export interface ConfirmOptions {
  /** Mensagem principal (obrigatória). */
  message: string;
  /** Título do modal. @default 'Confirmar' */
  title?: string;
  /** Aviso destacado opcional (caixa vermelha). */
  warningMessage?: string;
  /** Texto do botão de confirmar. @default 'Confirmar' */
  confirmButtonText?: string;
  /** Texto do botão de cancelar. @default 'Cancelar' */
  cancelButtonText?: string;
  /** 'danger' (vermelho, delete) ou 'default' (roxo, ação neutra). @default 'danger' */
  variant?: 'danger' | 'default';
  /**
   * Camada onde a caixa é desenhada. `'editor'` sobe a confirmação acima do
   * painel de nó e do seletor de nós do editor de workflows (`z-[201]`).
   * @default 'default'
   */
  layer?: 'default' | 'editor';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Retorna a função `confirm(options): Promise<boolean>`. Requer <ConfirmProvider> na árvore. */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm precisa de <ConfirmProvider> montado na árvore de componentes.');
  }
  return ctx;
};

const EMPTY: ConfirmOptions = { message: '' };

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{ open: boolean; options: ConfirmOptions }>({
    open: false,
    options: EMPTY,
  });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setState({ open: true, options });
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    setState((prev) => ({ ...prev, open: false }));
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <DeleteConfirmationModal
        isOpen={state.open}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={state.options.title ?? 'Confirmar'}
        message={state.options.message}
        warningMessage={state.options.warningMessage}
        confirmButtonText={state.options.confirmButtonText ?? 'Confirmar'}
        cancelButtonText={state.options.cancelButtonText ?? 'Cancelar'}
        variant={state.options.variant ?? 'danger'}
        layer={state.options.layer ?? 'default'}
      />
    </ConfirmContext.Provider>
  );
};

ConfirmProvider.displayName = 'ConfirmProvider';
